import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  SelectChangeEvent
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend
} from 'recharts';
import {
  Refresh as RefreshIcon,
  Analytics as AnalyticsIcon,
  LocationOn as LocationIcon,
  TrendingUp as TrendingUpIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`distance-tabpanel-${index}`}
      aria-labelledby={`distance-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface DistanceBreakdown {
  guestCount: number;
  totalRevenue: number;
  averageStay: number;
  averageSpend: number;
  repeatVisitRate: number;
}

interface AnalyticsData {
  _id: string;
  locationId: string;
  timeFrame: string;
  distanceBreakdown: {
    local: DistanceBreakdown;
    regional: DistanceBreakdown;
    longDistance: DistanceBreakdown;
    international: DistanceBreakdown;
  };
  topOriginCities: Array<{
    city: string;
    state: string;
    guestCount: number;
    revenue: number;
    averageDistance: number;
  }>;
  marketingInsights: {
    primaryMarket: {
      category: string;
      percentage: number;
      revenue: number;
    };
    growthOpportunities: Array<{
      market: string;
      potential: string;
      reasoning: string;
      recommendedActions: string[];
    }>;
  };
  generatedAt: string;
}

interface Location {
  _id: string;
  name: string;
  code: string;
}

const DrivingDistancePage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [timeFrame, setTimeFrame] = useState<string>('monthly');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [trendsData, setTrendsData] = useState<any[]>([]);
  const [distanceCalculatorOpen, setDistanceCalculatorOpen] = useState(false);
  const [bulkProcessOpen, setBulkProcessOpen] = useState(false);

  // Distance Calculator State
  const [fromAddress, setFromAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });
  const [toAddress, setToAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US'
  });
  const [calculatedDistance, setCalculatedDistance] = useState<any>(null);

  useEffect(() => {
    fetchLocations();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchAnalytics();
      fetchTrends();
    }
  }, [selectedLocation, timeFrame]);

  const fetchLocations = async () => {
    try {
      const response = await fetch('/api/locations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setLocations(data);
        if (data.length > 0) {
          setSelectedLocation(data[0]._id);
        }
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setError('Failed to load locations');
    }
  };

  const fetchAnalytics = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `/api/driving-distance/analytics/${selectedLocation}?timeFrame=${timeFrame}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setError('Failed to load analytics data');
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTrends = async () => {
    if (!selectedLocation) return;
    
    try {
      const response = await fetch(
        `/api/driving-distance/trends/${selectedLocation}?months=6`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTrendsData(data.trends || []);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    }
  };

  const regenerateAnalytics = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `/api/driving-distance/generate-analytics/${selectedLocation}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ timeFrame })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAnalyticsData(data);
      } else {
        setError('Failed to regenerate analytics');
      }
    } catch (error) {
      console.error('Error regenerating analytics:', error);
      setError('Failed to regenerate analytics');
    } finally {
      setLoading(false);
    }
  };

  const calculateDistance = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/driving-distance/calculate-distance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ fromAddress, toAddress })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCalculatedDistance(data);
      } else {
        setError('Failed to calculate distance');
      }
    } catch (error) {
      console.error('Error calculating distance:', error);
      setError('Failed to calculate distance');
    } finally {
      setLoading(false);
    }
  };

  const bulkProcess = async () => {
    if (!selectedLocation) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `/api/driving-distance/bulk-process/${selectedLocation}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            dateRange: {
              start: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString(),
              end: new Date().toISOString()
            }
          })
        }
      );
      
      if (response.ok) {
        setBulkProcessOpen(false);
        fetchAnalytics(); // Refresh analytics
      } else {
        setError('Failed to bulk process reservations');
      }
    } catch (error) {
      console.error('Error bulk processing:', error);
      setError('Failed to bulk process reservations');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLocationChange = (event: SelectChangeEvent) => {
    setSelectedLocation(event.target.value);
  };

  const handleTimeFrameChange = (event: SelectChangeEvent) => {
    setTimeFrame(event.target.value);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      local: '#4caf50',
      regional: '#2196f3',
      longDistance: '#ff9800',
      international: '#f44336'
    };
    return colors[category] || '#757575';
  };

  const getCategoryLabel = (category: string) => {
    const labels: { [key: string]: string } = {
      local: 'Local (0-50 mi)',
      regional: 'Regional (51-250 mi)',
      longDistance: 'Long Distance (251-1000 mi)',
      international: 'International (1000+ mi)'
    };
    return labels[category] || category;
  };

  const chartData = analyticsData ? Object.entries(analyticsData.distanceBreakdown).map(([category, data]) => ({
    category: getCategoryLabel(category),
    guests: data.guestCount,
    revenue: data.totalRevenue,
    avgSpend: data.averageSpend
  })) : [];

  const pieData = analyticsData ? Object.entries(analyticsData.distanceBreakdown).map(([category, data]) => ({
    name: getCategoryLabel(category),
    value: data.guestCount,
    color: getCategoryColor(category)
  })).filter(item => item.value > 0) : [];

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Driving Distance Analysis
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  label="Location"
                >
                  {locations.map((location) => (
                    <MenuItem key={location._id} value={location._id}>
                      {location.name} ({location.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Time Frame</InputLabel>
                <Select
                  value={timeFrame}
                  onChange={handleTimeFrameChange}
                  label="Time Frame"
                >
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                  <MenuItem value="quarterly">Quarterly</MenuItem>
                  <MenuItem value="yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={5}>
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchAnalytics}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AnalyticsIcon />}
                  onClick={regenerateAnalytics}
                  disabled={loading}
                >
                  Regenerate
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CalculateIcon />}
                  onClick={() => setDistanceCalculatorOpen(true)}
                >
                  Calculator
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setBulkProcessOpen(true)}
                >
                  Bulk Process
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Overview" />
          <Tab label="Breakdown" />
          <Tab label="Top Cities" />
          <Tab label="Insights" />
          <Tab label="Trends" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {analyticsData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Guest Distribution by Distance
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        dataKey="value"
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                {Object.entries(analyticsData.distanceBreakdown).map(([category, data]) => (
                  <Grid item xs={6} key={category}>
                    <Card>
                      <CardContent>
                        <Typography variant="body2" color="textSecondary">
                          {getCategoryLabel(category)}
                        </Typography>
                        <Typography variant="h6">
                          {data.guestCount} guests
                        </Typography>
                        <Typography variant="body2">
                          {formatCurrency(data.totalRevenue)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Avg: {formatCurrency(data.averageSpend)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {analyticsData && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue and Guest Count by Distance Category
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip formatter={(value, name) => [
                    name === 'revenue' ? formatCurrency(value as number) : value,
                    name === 'revenue' ? 'Revenue' : name === 'guests' ? 'Guests' : 'Avg Spend'
                  ]} />
                  <Legend />
                  <Bar yAxisId="left" dataKey="guests" fill="#2196f3" name="Guests" />
                  <Bar yAxisId="right" dataKey="revenue" fill="#4caf50" name="Revenue" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {analyticsData && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Origin Cities
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>City</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell align="right">Guests</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Avg Distance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.topOriginCities.map((city, index) => (
                      <TableRow key={index}>
                        <TableCell>{city.city}</TableCell>
                        <TableCell>{city.state}</TableCell>
                        <TableCell align="right">{city.guestCount}</TableCell>
                        <TableCell align="right">{formatCurrency(city.revenue)}</TableCell>
                        <TableCell align="right">{city.averageDistance} mi</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {analyticsData && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Primary Market
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Chip 
                      label={getCategoryLabel(analyticsData.marketingInsights.primaryMarket.category)}
                      color="primary"
                    />
                    <Typography variant="h6">
                      {analyticsData.marketingInsights.primaryMarket.percentage}%
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Revenue: {formatCurrency(analyticsData.marketingInsights.primaryMarket.revenue)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Growth Opportunities
                  </Typography>
                  {analyticsData.marketingInsights.growthOpportunities.map((opportunity, index) => (
                    <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <Chip 
                            label={getCategoryLabel(opportunity.market)}
                            color={opportunity.potential === 'high' ? 'error' : opportunity.potential === 'medium' ? 'warning' : 'default'}
                          />
                          <Chip 
                            label={`${opportunity.potential} potential`}
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {opportunity.reasoning}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold" gutterBottom>
                          Recommended Actions:
                        </Typography>
                        {opportunity.recommendedActions.map((action, actionIndex) => (
                          <Typography key={actionIndex} variant="body2" sx={{ ml: 2 }}>
                            • {action}
                          </Typography>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Guest Trends Over Time
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={trendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                <Line type="monotone" dataKey="local" stroke="#4caf50" name="Local" />
                <Line type="monotone" dataKey="regional" stroke="#2196f3" name="Regional" />
                <Line type="monotone" dataKey="longDistance" stroke="#ff9800" name="Long Distance" />
                <Line type="monotone" dataKey="international" stroke="#f44336" name="International" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Distance Calculator Dialog */}
      <Dialog open={distanceCalculatorOpen} onClose={() => setDistanceCalculatorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Distance Calculator</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>From Address</Typography>
              <TextField
                fullWidth
                label="Street"
                value={fromAddress.street}
                onChange={(e) => setFromAddress(prev => ({ ...prev, street: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="City"
                value={fromAddress.city}
                onChange={(e) => setFromAddress(prev => ({ ...prev, city: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="State"
                value={fromAddress.state}
                onChange={(e) => setFromAddress(prev => ({ ...prev, state: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Zip Code"
                value={fromAddress.zipCode}
                onChange={(e) => setFromAddress(prev => ({ ...prev, zipCode: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>To Address</Typography>
              <TextField
                fullWidth
                label="Street"
                value={toAddress.street}
                onChange={(e) => setToAddress(prev => ({ ...prev, street: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="City"
                value={toAddress.city}
                onChange={(e) => setToAddress(prev => ({ ...prev, city: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="State"
                value={toAddress.state}
                onChange={(e) => setToAddress(prev => ({ ...prev, state: e.target.value }))}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Zip Code"
                value={toAddress.zipCode}
                onChange={(e) => setToAddress(prev => ({ ...prev, zipCode: e.target.value }))}
              />
            </Grid>
            {calculatedDistance && (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Distance: {calculatedDistance.distance.drivingMiles} miles
                    </Typography>
                    <Typography variant="body2">
                      Drive Time: {Math.floor(calculatedDistance.distance.drivingMinutes / 60)}h {calculatedDistance.distance.drivingMinutes % 60}m
                    </Typography>
                    <Typography variant="body2">
                      Category: {getCategoryLabel(calculatedDistance.distance.category)}
                    </Typography>
                    <Typography variant="body2">
                      Estimated Fuel Cost: {formatCurrency(calculatedDistance.distance.estimatedFuelCost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDistanceCalculatorOpen(false)}>Close</Button>
          <Button onClick={calculateDistance} variant="contained" disabled={loading}>
            Calculate Distance
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Process Dialog */}
      <Dialog open={bulkProcessOpen} onClose={() => setBulkProcessOpen(false)}>
        <DialogTitle>Bulk Process Reservations</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will process distance analysis for all reservations in the last 90 days.
            This may take several minutes.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkProcessOpen(false)}>Cancel</Button>
          <Button onClick={bulkProcess} variant="contained" disabled={loading}>
            Start Processing
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DrivingDistancePage;